import Modal from 'components/common/modal';
import Text from 'components/inputs/text';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { focusAndSelectInput } from 'utils/focus-and-select-input';

type ExportStructureModalProps = Readonly<{
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
  defaultName?: string;
  onConfirm: (name: string) => void;
}>;

export default function ExportStructureModal({
  openState,
  defaultName = '',
  onConfirm
}: ExportStructureModalProps) {
  const [open] = openState;
  const [editedName, setEditedName] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLElement | null>(null);
  const name = editedName ?? defaultName;

  useEffect(() => {
    if (open) {
      setEditedName(null);
    }
  }, [open, defaultName]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const immediateId = window.setTimeout(() => {
      focusAndSelectInput(nameInputRef.current);
    }, 0);
    const delayedId = window.setTimeout(() => {
      focusAndSelectInput(nameInputRef.current);
    }, 250);
    return () => {
      window.clearTimeout(immediateId);
      window.clearTimeout(delayedId);
    };
  }, [open]);

  const trimmedName = name.trim();
  const saveDisabled = trimmedName.length === 0;

  const handleSave = () => {
    if (saveDisabled) {
      return;
    }
    onConfirm(trimmedName);
  };

  return (
    <Modal
      title='Name Your Structure'
      buttonText='Export'
      openState={openState}
      onSave={handleSave}
      saveDisabled={saveDisabled}
      initialFocus={nameInputRef}
    >
      <div className='flex flex-col gap-2'>
        <label
          htmlFor='export-structure-name'
          className='font-semibold text-gray-900'
        >
          Structure Name
        </label>
        <Text
          id='export-structure-name'
          value={name}
          onChange={setEditedName}
          placeholder='Enter a name for the exported structure'
          autoFocus
          autoSelect
          inputRef={nameInputRef}
          onFocus={(event) => {
            focusAndSelectInput(event.currentTarget);
          }}
        />
        <div className='text-xs text-gray-500'>
          The file will be saved as{' '}
          <span className='font-medium text-gray-700'>
            {trimmedName.length > 0 ? trimmedName : 'structure'}.json
          </span>
        </div>
      </div>
    </Modal>
  );
}
